import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Error message component for displaying user-friendly error messages
 * 
 * This component provides a standardized way to display error messages
 * throughout the application with proper accessibility support.
 */
interface ErrorMessageProps {
  /** The error message to display */
  message: string;
  /** Optional detailed error information for developers */
  details?: string;
  /** Optional retry callback */
  onRetry?: () => void;
  /** Optional CSS class name */
  className?: string;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
  message,
  details,
  onRetry,
  className = '',
}) => {
  return (
    <div 
      className={`w-full my-4 rounded-2xl border border-red-500/20 bg-red-50/50 backdrop-blur-md p-5 shadow-sm ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle size={20} className="text-red-600" />
          <h3 className="text-base font-semibold m-0">
            Error
          </h3>
        </div>
        <p className="text-sm text-red-800/90 m-0 leading-relaxed">
          {message}
        </p>
        
        {details && (
          <details className="mt-2 text-sm text-red-900/80 cursor-pointer group">
            <summary className="font-medium outline-none select-none group-hover:text-red-700 transition-colors">
              Technical Details
            </summary>
            <pre className="mt-3 p-4 bg-red-950/5 rounded-xl overflow-x-auto font-mono text-xs text-red-900 border border-red-900/10 custom-scrollbar">
              {details}
            </pre>
          </details>
        )}
        
        {onRetry && (
          <div className="mt-4 flex items-center">
            <button
              onClick={onRetry}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all shadow-sm active:scale-[0.98]"
            >
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorMessage; 